import React from 'react';
import { 
    Box, Typography, IconButton, Grid, Avatar, Paper, 
    Chip, Stack, useTheme 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCalendarAlt, faClock, faStore, faMinus, faPlus, faLayerGroup, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import 'moment/locale/es'; 

export default function CartItemRow({ item, onRemove, onUpdateQty }) {
    const theme = useTheme();
    const isBooking = item.type === 'booking';
    
    // Extracción segura de datos
    const product = item.product || {};
    const options = item.options || {}; // Accedemos a todo el objeto options
    
    // Desestructuramos las partes clave
    const booking = options.booking || null;
    const variants = options.variants || {}; // Usualmente es objeto { Color: "Rojo" }
    const modifiers = options.modifiers || []; // Usualmente es array [{ name: "Extra Queso", price: 2 }]

    // Helper para formatear precio de extras si existe
    const formatExtraPrice = (price) => {
        const val = parseFloat(price);
        return val > 0 ? `(+$${val.toFixed(2)})` : '';
    };

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 2, 
                mb: 2, 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 3,
                transition: 'all 0.2s',
                '&:hover': { borderColor: theme.palette.primary.light }
            }}
        >
            <Grid container spacing={2} alignItems="center">
                
                {/* 1. IMAGEN */}
                <Grid item xs={3} sm={2} md={2}>
                    <Avatar 
                        variant="rounded"
                        src={product.image}
                        sx={{ 
                            width: '100%', 
                            height: 80, 
                            bgcolor: 'grey.100',
                            border: '1px solid #f0f0f0'
                        }}
                    >
                        <FontAwesomeIcon icon={faStore} color="#ccc"/>
                    </Avatar>
                </Grid>

                {/* 2. DETALLES DEL ITEM */}
                <Grid item xs={9} sm={5} md={6}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        {product.name}
                    </Typography>

                    {/* --- A. SI ES RESERVA --- */}
                    {isBooking && booking && (
                        <Box mt={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap" gap={0.5}>
                                <Chip 
                                    icon={<FontAwesomeIcon icon={faCalendarAlt} size="xs"/>} 
                                    label={booking.display_date || moment(booking.start_datetime).format('D MMM YYYY')} 
                                    size="small" 
                                    sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', border: 'none' }}
                                />
                                <Chip 
                                    icon={<FontAwesomeIcon icon={faClock} size="xs"/>} 
                                    label={booking.display_time || moment(booking.start_datetime).format('h:mm A')} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ borderColor: '#e3f2fd', color: '#555' }}
                                />
                            </Stack>
                            
                            {booking.resource_name && (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Avatar src={booking.resource_image} sx={{ width: 22, height: 22 }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Atiende: <strong>{booking.resource_name}</strong>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* --- B. VARIANTES (Talla, Color) --- */}
                    {!isBooking && Object.keys(variants).length > 0 && (
                        <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                            {Object.entries(variants).map(([key, val]) => (
                                <Chip 
                                    key={key} 
                                    icon={<FontAwesomeIcon icon={faLayerGroup} size="xs" />}
                                    label={`${key}: ${val}`} 
                                    size="small" 
                                    sx={{ height: 24, fontSize: '0.75rem', bgcolor: '#f5f5f5' }} 
                                />
                            ))}
                        </Box>
                    )}

                    {/* --- C. EXTRAS / MODIFICADORES --- */}
                    {!isBooking && modifiers.length > 0 && (
                        <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                            {modifiers.map((mod, index) => (
                                <Chip 
                                    key={index}
                                    icon={<FontAwesomeIcon icon={faPlusCircle} size="xs" />}
                                    // Detectamos si 'mod' es string simple o un objeto con precio
                                    label={typeof mod === 'string' ? mod : `${mod.name} ${formatExtraPrice(mod.price)}`} 
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    sx={{ height: 24, fontSize: '0.75rem', borderColor: theme.palette.success.light }} 
                                />
                            ))}
                        </Box>
                    )}
                </Grid>

                {/* 3. CANTIDAD */}
                <Grid item xs={6} sm={3} md={2}>
                    {isBooking ? (
                        <Typography variant="body2" color="text.secondary" textAlign={{ xs: 'left', md: 'center' }}>
                            1 Servicio
                        </Typography>
                    ) : (
                        <Box 
                            display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'center' }} 
                            border="1px solid #e0e0e0" borderRadius={50} width="fit-content" bgcolor="#fff"
                        >
                            <IconButton 
                                size="small" 
                                onClick={() => onUpdateQty(item.item_id, Math.max(1, item.quantity - 1))}
                                disabled={item.quantity <= 1}
                            >
                                <FontAwesomeIcon icon={faMinus} size="xs" color="#888"/>
                            </IconButton>
                            
                            <Typography variant="body2" fontWeight="bold" sx={{ mx: 1.5, minWidth: 15, textAlign: 'center' }}>
                                {item.quantity}
                            </Typography>
                            
                            <IconButton 
                                size="small" 
                                onClick={() => onUpdateQty(item.item_id, item.quantity + 1)}
                            >
                                <FontAwesomeIcon icon={faPlus} size="xs" color="#888"/>
                            </IconButton>
                        </Box>
                    )}
                </Grid>

                {/* 4. TOTAL Y ACCIONES */}
                <Grid item xs={6} sm={2} md={2} display="flex" flexDirection="column" alignItems="flex-end">
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                        ${parseFloat(item.total_line).toFixed(2)}
                    </Typography>
                    
                    {item.quantity > 1 && !isBooking && (
                        <Typography variant="caption" color="text.secondary">
                            ${parseFloat(item.unit_price).toFixed(2)} c/u
                        </Typography>
                    )}

                    <IconButton 
                        size="small" color="error" 
                        sx={{ mt: 1, bgcolor: '#fff0f0', '&:hover': { bgcolor: '#ffe0e0' } }}
                        onClick={() => onRemove(item.item_id)}
                    >
                        <FontAwesomeIcon icon={faTrash} size="xs" />
                    </IconButton>
                </Grid>
            </Grid>
        </Paper>
    );
}